import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next'

import { ExtendedGetServerSideProps, ServerCachedContext } from '#utils/types'

import { createServerGyymApiClient } from '../utils/createServerGyymApiClient'

export const composeServerProps = <P extends object>(
  ...fns: ExtendedGetServerSideProps<P>[]
) => {
  return async (
    ctx: GetServerSidePropsContext,
    { client: cachedClient }: ServerCachedContext<P> = {},
  ) => {
    const propsMap = new Map()

    // Always create a new client instance per request to prevent sessions leaking
    const client = cachedClient ?? createServerGyymApiClient({ ctx })

    // Iterate through each async function
    for (const fn of fns) {
      const result = await fn(ctx, {
        client,
        composedProps: Object.fromEntries(propsMap),
      })

      // If notFound is in the result, return early
      if ('notFound' in result) {
        return {
          notFound: result.notFound,
        }
      }

      // If redirect is in the result, return early
      if ('redirect' in result) {
        return {
          redirect: result.redirect,
        }
      }

      if ('props' in result) {
        // Iterate through each key in the props object
        for (const [key, value] of Object.entries(result.props)) {
          // If the key is not in the propsMap, set it
          if (!propsMap.has(key)) {
            propsMap.set(key, value)
          } else {
            const existingValue = propsMap.get(key)

            // Merge swr fallback config
            if (key === 'swr') {
              propsMap.set(key, {
                ...existingValue,
                ...value,
                fallback: {
                  ...existingValue?.fallback,
                  ...value?.fallback,
                },
              })
            } else {
              propsMap.set(key, value)
            }
          }
        }
      }
    }

    const result: GetServerSidePropsResult<P> = {
      props: Object.fromEntries(propsMap),
    }

    return result
  }
}
